import React from 'react';
import Module, { ModuleDescriptions } from '../../constants/Modules';

/**
 * Component for managing clinic modules
 * @param {Object} props
 * @param {Array} props.modules - Current enabled modules
 * @param {Function} props.onChange - Callback when modules change, receives array of module names
 * @param {boolean} props.readOnly - If true, renders in read-only mode (no checkboxes)
 */
const ModulesSection = ({ modules = [], onChange, readOnly = false }) => {
  // Function to toggle a module
  const toggleModule = (moduleName, isEnabled) => {
    if (readOnly) return;

    let updatedModules;
    if (isEnabled) {
      // Add module if not present
      updatedModules = modules.includes(moduleName)
        ? modules
        : [...modules, moduleName];
    } else {
      // Remove module if present
      updatedModules = modules.filter(m => m !== moduleName);
    }

    onChange(updatedModules);
  };

  // Get all available modules
  const availableModules = Object.values(Module);

  // Get display name for a module
  const getModuleDisplayName = (moduleName) => {
    if (moduleName === Module.QA_KNOWLEDGE_BASE) return 'QA Knowledge Base';
    if (moduleName === Module.RAG_KNOWLEDGE_BASE) return 'RAG Knowledge Base';
    if (moduleName === Module.STRUCTURED_DATA) return 'Structured Data';
    if (moduleName === Module.ACRM) return 'Agent CRM';
    if (moduleName === Module.INTEGRATION_SG_CLINIC_ASSIST) return 'Clinic Assistant Integration';
    if (moduleName === Module.PORTAL_APPOINTMENT) return 'Portal Appointment';
    if (moduleName === Module.PORTAL_IMPORT) return 'Portal Import';
    return moduleName;
  };

  return (
    <div className="space-y-4">
      <h4 className="text-md font-semibold mb-2">
        {readOnly ? 'Enabled Modules' : 'Modules'}
      </h4>

      {readOnly ? (
        // Read-only view
        <div className="flex flex-wrap gap-2">
          {modules && modules.length > 0 ? (
            modules.map(module => (
              <span key={module} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                {getModuleDisplayName(module)}
              </span>
            ))
          ) : (
            <span className="text-gray-500">No modules enabled</span>
          )}
        </div>
      ) : (
        // Edit mode
        <div className="space-y-3">
          {availableModules.map(moduleName => (
            <div key={moduleName} className="flex items-start space-x-4">
              <div className="flex items-center h-5">
                <input
                  id={`module_${moduleName}`}
                  name={`module_${moduleName}`}
                  type="checkbox"
                  checked={modules.includes(moduleName)}
                  onChange={(e) => toggleModule(moduleName, e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor={`module_${moduleName}`} className="font-medium text-gray-700">
                  {getModuleDisplayName(moduleName)}
                </label>
                <p className="text-gray-500">
                  {ModuleDescriptions[moduleName] || `Enables the ${moduleName} feature.`}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModulesSection;
